import React, { Suspense, lazy, useEffect, useState, useCallback, useRef } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";

import LoadingIntro from "./components/LoadingIntro";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import ServersSection from "./components/ServersSection";
import VipSection from "./components/VipSection";
import KitsSection from "./components/KitsSection";
import HowToBuy from "./components/HowToBuy";
import DiscordCTA from "./components/DiscordCTA";
import Footer from "./components/Footer";
import LoginModal from "./components/LoginModal";
import VipSimulator from "./components/vip-simulator/VipSimulator";
import RequirePlayerAuth from "./components/auth/RequirePlayerAuth";
import UserDashboard from "./components/UserDashboard";
import ZaroChat from "./components/zaro/ZaroChat";
import { ADMIN_PATH } from "./lib/adminPortal";

/** Portal admin num chunk à parte: só é descarregado por quem abre o caminho. */
const AdminPanel = lazy(() => import("./components/admin/AdminPanel"));
import { supabase, supabaseConfigured } from "./lib/supabaseClient";
import { scrollToSectionId } from "./lib/scrollToSection";
import { Toaster, toast } from "sonner";
import { useI18n } from "./i18n/I18nProvider";

const DISCORD_URL = "https://discord.gg/QK8N5xPNAC";
const INTRO_SEEN_KEY = "zrs_intro_seen";

function hasSeenIntroThisSession() {
    try {
        return sessionStorage.getItem(INTRO_SEEN_KEY) === "1";
    } catch {
        return false;
    }
}

/** Erro devolvido no callback OAuth (query ou hash) quando a troca do código falha no Supabase. */
function readOAuthCallbackError() {
    try {
        const url = new URL(window.location.href);
        const q = url.searchParams;
        let err = q.get("error");
        let desc = q.get("error_description");
        if (url.hash && url.hash.length > 1) {
            const hp = new URLSearchParams(url.hash.replace(/^#/, ""));
            err = err || hp.get("error");
            desc = desc || hp.get("error_description");
        }
        if (!err) return null;
        const text = desc
            ? decodeURIComponent(String(desc).replace(/\+/g, " "))
            : null;
        return { err, description: text };
    } catch {
        return null;
    }
}

export default function App() {
    const [loading, setLoading] = useState(
        () => !hasSeenIntroThisSession() && window.location.pathname !== ADMIN_PATH,
    );
    const [loginOpen, setLoginOpen] = useState(false);
    const [session, setSession] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isAdminRoute = location.pathname === ADMIN_PATH;
    const hideMainShell = location.pathname === "/vip/personalizado" || isAdminRoute;
    const oauthErrorToastRef = useRef(false);
    const { t } = useI18n();

    useEffect(() => {
        if (oauthErrorToastRef.current) return;
        const fail = readOAuthCallbackError();
        if (!fail) return;
        oauthErrorToastRef.current = true;
        console.warn("[auth/oauth-callback]", fail);
        const isExchange =
            fail.description?.includes("exchange") ||
            fail.description?.includes("Unable to exchange");
        toast.error(
            isExchange ? t("login.errorExchange") : t("login.errorComplete"),
            {
                description: fail.description || fail.err,
                duration: 14_000,
            },
        );
        navigate("/", { replace: true });
    }, [navigate, t]);

    useEffect(() => {
        if (!loading) {
            document.documentElement.style.scrollBehavior = "smooth";
        }
    }, [loading]);

    // Scroll para secção quando a URL traz hash (ex. /#vips vindo de outra rota).
    useEffect(() => {
        if (loading || hideMainShell || location.pathname !== "/") return undefined;
        const sectionId = location.hash?.replace(/^#/, "");
        if (!sectionId) return undefined;
        const frame = window.requestAnimationFrame(() => {
            scrollToSectionId(sectionId);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [loading, hideMainShell, location.pathname, location.hash]);

    useEffect(() => {
        if (!supabaseConfigured || !supabase) {
            setSession(null);
            return undefined;
        }

        supabase.auth.getSession().then(({ data: { session: s } }) => {
            setSession(s);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, s) => {
            setSession(s);
            if (s) setLoginOpen(false);
        });

        return () => subscription.unsubscribe();
    }, []);

    const handleSignOut = useCallback(async () => {
        if (supabase) {
            await supabase.auth.signOut();
        }
        setSession(null);
        navigate("/", { replace: true });
    }, [navigate]);

    return (
        <div className="App grain vignette">
            {loading && (
                <LoadingIntro
                    onDone={() => {
                        try {
                            sessionStorage.setItem(INTRO_SEEN_KEY, "1");
                        } catch {
                            /* ignore */
                        }
                        setLoading(false);
                    }}
                />
            )}

            {!hideMainShell && (
                <Navbar
                    onLoginClick={() => setLoginOpen(true)}
                    onSignOut={handleSignOut}
                    discordUrl={DISCORD_URL}
                    user={session?.user ?? null}
                />
            )}

            <Routes>
                <Route
                    path="/conta"
                    element={
                        <UserDashboard
                            user={session?.user ?? null}
                            discordUrl={DISCORD_URL}
                        />
                    }
                />
                <Route
                    path="/vip/personalizado"
                    element={
                        <RequirePlayerAuth
                            user={session?.user ?? null}
                            onLoginClick={() => setLoginOpen(true)}
                            title={t("login.simulatorTitle")}
                        >
                            <VipSimulator user={session?.user ?? null} />
                        </RequirePlayerAuth>
                    }
                />
                <Route
                    path="/"
                    element={
                        <main>
                            <Hero discordUrl={DISCORD_URL} />
                            <ServersSection />
                            <VipSection />
                            <KitsSection />
                            <HowToBuy />
                            <DiscordCTA discordUrl={DISCORD_URL} />
                        </main>
                    }
                />
                <Route
                    path={ADMIN_PATH}
                    element={
                        <Suspense fallback={<div className="min-h-screen bg-[#050505]" />}>
                            <AdminPanel user={session?.user ?? null} />
                        </Suspense>
                    }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {!hideMainShell && <Footer discordUrl={DISCORD_URL} />}

            <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

            {!isAdminRoute && !loading && (
                <ZaroChat user={session?.user ?? null} onLoginClick={() => setLoginOpen(true)} />
            )}

            <Toaster
                theme="dark"
                position="bottom-right"
                richColors
                closeButton
                offset={{ bottom: 100, right: 24 }}
                mobileOffset={{ bottom: 96 }}
            />
        </div>
    );
}
